import React, { useEffect, useState, useContext } from "react";
import AddTemplateForm from "../components/settings/AddTemplateForm";
import { ApiContext } from '../App';
import { Mail, Plus, Search, Edit2, Trash2, X, Save, AlertTriangle } from 'lucide-react';
import RichTextEditor from '../components/common/RichTextEditor';

const EmailTemplatesPage = () => {
  const API = useContext(ApiContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", subject: "", body: "", tags: [] });
  const [tagInput, setTagInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await API.get("/api/templates");
      setTemplates(response.data.templates);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement des templates :", error);
      setError("Impossible de charger les templates. Veuillez réessayer plus tard.");
      setLoading(false);
    }
  };

  const closeAddTemplateForm = () => {
    setShowAddForm(false);
  };

  const deleteTemplate = async (event) => {
    try {
      await API.delete(`/api/templates/${templateToDelete}`);
      fetchTemplates();
      setTemplateToDelete(null);
      setShowModal(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du template :", error);
      setError("Erreur lors de la suppression du template.");
    }
  };

  const confirmDelete = (id, event) => {
    event.stopPropagation();
    setTemplateToDelete(id);
    setShowModal(true);
  };

  const cancelDelete = () => {
    setShowModal(false);
    setTemplateToDelete(null);
  };

  const editTemplate = async (id, event) => {
    if (event.target.closest('button')) {
      return;
    }
    const templateToEdit = templates.find(template => template._id === id);
    setEditingTemplate(templateToEdit);
    setEditFormData({
      name: templateToEdit.name,
      subject: templateToEdit.subject,
      body: templateToEdit.body,
      tags: templateToEdit.tags || []
    });
    setShowModal(true);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !editFormData.tags.includes(tagInput.trim().toLowerCase())) {
      setEditFormData({
        ...editFormData,
        tags: [...editFormData.tags, tagInput.trim().toLowerCase()]
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditFormData({
      ...editFormData,
      tags: editFormData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/api/templates/${editingTemplate._id}`, editFormData);
      fetchTemplates();
      setShowModal(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error("Erreur lors de la modification du template :", error);
      setError("Erreur lors de la modification du template.");
    }
  };

  const handleEditChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleBodyChange = (value) => {
    setEditFormData({
      ...editFormData,
      body: value
    });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(search.toLowerCase()) ||
    template.subject.toLowerCase().includes(search.toLowerCase())
  );

  const formatTemplatePreview = (body) => {
    // Supprimer les balises HTML si présentes
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = body;
    const textContent = tempDiv.textContent || tempDiv.innerText || body;
    
    // Limiter la longueur du texte prévisualisé
    return textContent.length > 80
      ? textContent.substring(0, 80) + '...' 
      : textContent;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="p-4 sm:p-6 rounded-lg bg-white min-h-screen border">
      <header className="mb-6 sm:mb-8">
        <h2 className='text-lg sm:text-xl font-semibold mb-2'>Templates Email</h2>
        <p className="text-sm sm:text-base text-slate-600">Gagnez du temps en créant vos propres modèles de message. N'hésitez pas à y ajouter des champs dynamiques et des liens de prise rendez-vous !</p>
      </header>

      {/* Barre d'outils */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <button
          onClick={() => setShowAddForm(true)} 
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 shadow-sm text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
        >
          <Plus size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Nouveau template</span>
          <span className="sm:hidden">Nouveau</span>
        </button>

        <div className="relative w-full sm:w-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="sm:w-5 sm:h-5 text-slate-400" />
          </div>
          <input
            type="text"
            className="pl-9 sm:pl-10 pr-4 py-2 w-full sm:w-64 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Résultats de recherche */}
      {search && (
        <div className="text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4">
          {filteredTemplates.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Messages d'erreur */}
      {error && (
        <div className="flex items-center gap-2 p-3 sm:p-4 mb-4 sm:mb-6 bg-red-50 text-red-700 rounded-lg">
          <AlertTriangle size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
          <p className="text-sm sm:text-base">{error}</p>
        </div>
      )}

      {/* Contenu principal */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-16 px-4">
          <Mail size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-2">
            {search ? "Aucun template ne correspond à votre recherche" : "Aucun template disponible"}
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Créer votre premier template
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {/* Vue mobile - cartes */}
          <div className="block sm:hidden">
            <div className="space-y-3 p-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template._id}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={(e) => editTemplate(template._id, e)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-slate-900 truncate">{template.name}</h3>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const templateToEdit = templates.find(t => t._id === template._id);
                          setEditingTemplate(templateToEdit);
                          setEditFormData({
                            name: templateToEdit.name,
                            subject: templateToEdit.subject,
                            body: templateToEdit.body,
                            tags: templateToEdit.tags || []
                          });
                          setShowModal(true);
                        }}
                        className="p-1.5 rounded-full hover:bg-gray-200 text-slate-600 hover:text-indigo-600 transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={(e) => confirmDelete(template._id, e)}
                        className="p-1.5 rounded-full hover:bg-gray-200 text-slate-600 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mb-2 space-y-2">
                    <span className="inline-block bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs font-medium">
                      {template.subject}
                    </span>
                    {template.tags && template.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {template.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-slate-600 text-sm mb-2 line-clamp-2">
                    {formatTemplatePreview(template.body)}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      {template.user?.photo ? (
                        <img
                          src={template.user.photo}
                          alt={template.user.name || 'User'}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">
                          {template.user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <span>{template.user?.name || 'Inconnu'}</span>
                    </div>
                    <div>
                      {template.createdAt ? formatDate(template.createdAt) : 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Vue desktop - tableau */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Nom du template</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Sujet</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Aperçu du contenu</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Créé par</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Date de création</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTemplates.map((template) => (
                  <tr
                    key={template._id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={(e) => editTemplate(template._id, e)}
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900 text-sm">{template.name}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-2">
                        <div className="inline-block bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs font-medium">
                          {template.subject}
                        </div>
                        {template.tags && template.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {template.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-600 text-xs max-w-xs">
                        {formatTemplatePreview(template.body)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {template.user?.photo ? (
                          <img
                            src={template.user.photo}
                            alt={template.user.name || 'User'}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">
                            {template.user?.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                        )}
                        <span className="text-slate-700 text-xs">{template.user?.name || 'Inconnu'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-500 text-xs">
                        {template.createdAt ? formatDate(template.createdAt) : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const templateToEdit = templates.find(t => t._id === template._id);
                            setEditingTemplate(templateToEdit);
                            setEditFormData({
                              name: templateToEdit.name,
                              subject: templateToEdit.subject,
                              body: templateToEdit.body
                            });
                            setShowModal(true);
                          }}
                          className="p-2 rounded-full hover:bg-gray-200 text-slate-600 hover:text-indigo-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => confirmDelete(template._id, e)}
                          className="p-2 rounded-full hover:bg-gray-200 text-slate-600 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal d'ajout de template */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Créer un template</h2>
              <button 
                onClick={() => setShowAddForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} className="sm:w-5 sm:h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-60px)] sm:max-h-[calc(90vh-80px)]">
              <AddTemplateForm 
                onTemplateAdded={fetchTemplates} 
                onClose={() => setShowAddForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showModal && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl w-full max-w-xs sm:max-w-md">
            <div className="flex items-center text-red-600 mb-3 sm:mb-4">
              <AlertTriangle size={20} className="sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-semibold">Supprimer ce template ?</h3>
            </div>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              Cette action est irréversible. Souhaitez-vous vraiment supprimer ce template ?
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base order-2 sm:order-1"
              >
                Annuler
              </button>
              <button
                onClick={deleteTemplate}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm sm:text-base order-1 sm:order-2"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {showModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Modifier le template</h2>
              <button 
                onClick={() => {setShowModal(false); setEditingTemplate(null)}}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} className="sm:w-5 sm:h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-60px)] sm:max-h-[calc(90vh-80px)]">
              <form onSubmit={handleEditSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                    Nom du template
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-1">
                    Sujet de l'email
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={editFormData.subject}
                    onChange={handleEditChange}
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700 mb-1">
                    Tags (optionnel)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="flex-1 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ex: recrutement, relance..."
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                    >
                      Ajouter
                    </button>
                  </div>
                  {editFormData.tags && editFormData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-indigo-900"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    Les tags facilitent la recherche de templates
                  </p>
                </div>

                <div>
                  <label htmlFor="body" className="block text-sm font-medium text-slate-700 mb-1 ">
                    Corps du message
                  </label>
                  <div className="border-1 border-gray-200 rounded-lg">
                     <RichTextEditor
                    value={editFormData.body}
                    onChange={handleBodyChange}
                    placeholder="Bonjour {{Name}}, j'espère que vous allez bien..."
                    minHeight="300px"
                  />
                  </div>
                 
                  <div className="mt-2 text-xs text-slate-500">
                    Variables disponibles: "&#123;&#123;Name&#125;&#125;", "&#123;&#123;company&#125;&#125;", "&#123;&#123;jobTitle&#125;&#125;", "&#123;&#123;city&#125;&#125;"
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {setShowModal(false); setEditingTemplate(null)}}
                    className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base order-2 sm:order-1"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm sm:text-base order-1 sm:order-2"
                  >
                    <Save size={16} className="sm:w-5 sm:h-5 mr-2" />
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesPage;